<template>
    <span style="display:none"></span>
</template>

<script>
const firedTokens = new Set();

export default {
    name: 'tracker',

    props: {
        token: {
            type: String,
            required: true,
        },
        url: {
            type: String,
            default: 'https://signal.birchplace.fans/beacon/pulse',
        },
        min: {
            type: Number,
            default: 5,
        },
        active: {
            type: Boolean,
            default: true,
        },
        debug: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            startedAt: 0,
            fired: false,
            started: false,
            sessionId: 0,
        };
    },

    mounted() {
        this.bindEvents();

        if (this.active) {
            this.start();
        }
    },

    beforeUnmount() {
        this.send('destroyed');
        this.cleanup();
    },

    watch: {
        token(newVal, oldVal) {
            if (!newVal || newVal === oldVal) return;

            this.log('token changed', oldVal, '=>', newVal);

            this.started = false;
            this.startedAt = 0;
            this.fired = false;

            if (this.active) {
                this.start();
            }
        },

        active(newVal, oldVal) {
            if (newVal === oldVal) return;

            if (oldVal === true && newVal === false) {
                this.send('modal_closed');
                this.started = false;
                return;
            }

            if (oldVal === false && newVal === true) {
                this.start();
            }
        }
    },

    methods: {
        log() {
            if (!this.debug) return;
            console.log.apply(console, ['[tracker]'].concat([].slice.call(arguments)));
        },

        bindEvents() {
            document.addEventListener('visibilitychange', this.onVisibilityChange);
            window.addEventListener('pagehide', this.onPageHide);
            window.addEventListener('beforeunload', this.onBeforeUnload);
        },

        cleanup() {
            document.removeEventListener('visibilitychange', this.onVisibilityChange);
            window.removeEventListener('pagehide', this.onPageHide);
            window.removeEventListener('beforeunload', this.onBeforeUnload);
        },

        start() {
            if (!this.token) {
                this.log('start skipped: no token');
                return;
            }

            if (!this.active) {
                this.log('start skipped: not active');
                return;
            }

            if (this.started) {
                this.log('start skipped: already started');
                return;
            }

            if (this.fired || firedTokens.has(this.token)) {
                this.log('start skipped: already fired for token', this.token);
                return;
            }

            this.started = true;
            this.startedAt = Date.now();
            this.sessionId += 1;

            this.log('started', {
                token: this.token,
                sessionId: this.sessionId,
                min: this.min
            });
        },

        seconds() {
            if (!this.startedAt) return 0;
            return Math.floor((Date.now() - this.startedAt) / 1000);
        },

        markFired() {
            this.fired = true;
            this.started = false;

            if (this.token) {
                firedTokens.add(this.token);
            }
        },

        send(reason) {
            if (!this.token) {
                this.log('send skipped: no token', reason);
                return;
            }

            if (this.fired || firedTokens.has(this.token)) {
                this.log('send skipped: already fired', reason);
                return;
            }

            const sec = this.seconds();

            if (sec < this.min) {
                this.log('skip < min', reason, sec + 's');
                return;
            }

            this.markFired();

            const body = new URLSearchParams();
            body.set('x', this.token);
            body.set('r', reason);
            body.set('sec', String(sec));

            if (this.debug) {
                body.set('debug', '1');
            }

            this.log('sending', {
                reason,
                sec,
                token: this.token
            });

            try {
                if (navigator.sendBeacon) {
                    const blob = new Blob([body.toString()], {
                        type: 'application/x-www-form-urlencoded'
                    });

                    if (navigator.sendBeacon(this.url, blob)) {
                        this.log('beacon ok', reason, sec + 's');
                        return;
                    }

                    this.log('beacon returned false', reason);
                }
            } catch (e) {
                this.log('beacon error', e);
            }

            fetch(this.url, {
                method: 'POST',
                body: body.toString(),
                keepalive: true,
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            })
                .then((res) => {
                    this.log('fetch ok', reason, 'status=' + res.status);
                })
                .catch((e) => {
                    this.log('fetch error', e);
                });
        },

        onVisibilityChange() {
            if (document.visibilityState === 'hidden') {
                this.send('hidden');
            }
        },

        onPageHide() {
            this.send('pagehide');
        },

        onBeforeUnload() {
            this.send('beforeunload');
        }
    }
}
</script>
